import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatAddress } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

/* Fix U (2026-10-05): revisión de datos maestros. Muestra lo que hay que ordenar (compañías
   repetidas, casas cargadas como edificio, etc.) y deja resolverlo de a un paso, siempre con
   confirmación. Nada se cambia solo. */

const MARCA = { bb: "B&B", betos: "Betos" };

const Seccion = ({ titulo, desc, n, children, testId }) => (
  <section className="border-t border-line pt-4" data-testid={testId}>
    <p className="text-[13px] font-semibold text-ink">{titulo} <span className="font-mono text-ink-tertiary">({n})</span></p>
    <p className="mb-2 text-[12px] text-ink-tertiary">{desc}</p>
    {n ? children : <p className="text-[12px] text-ink-tertiary">—</p>}
  </section>
);

export const MergeCompanyDialog = ({ open, onOpenChange, company, onMerged }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const [destino, setDestino] = useState("");
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({
    queryKey: ["companies-merge-options"],
    queryFn: async () => (await api.get("/companies", { params: { page_size: 200 } })).data,
    enabled: open,
  });
  const opciones = (data?.items || []).filter((c) => c.id !== company?.id);
  const elegido = opciones.find((c) => c.id === destino);

  const unir = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/companies/${company.id}/merge`, { into_id: destino });
      toast.success(res.detalle || t("master_data.merged"));
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["company"] });
      qc.invalidateQueries({ queryKey: ["master-data-review"] });
      onOpenChange(false);
      onMerged?.(destino);
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setDestino(""); onOpenChange(v); }}>
      <DialogContent data-testid="merge-company-dialog" className="max-w-[520px] rounded-[8px]">
        <DialogHeader>
          <DialogTitle className="text-[16px]">{t("master_data.merge_title", { nombre: company?.nombre || "" })}</DialogTitle>
          <DialogDescription className="text-[12.5px] text-ink-secondary">{t("master_data.merge_desc")}</DialogDescription>
        </DialogHeader>
        <Select value={destino} onValueChange={setDestino}>
          <SelectTrigger data-testid="merge-company-target" className="h-9 rounded-[6px] text-[13px]">
            <SelectValue placeholder={t("master_data.merge_target")} />
          </SelectTrigger>
          <SelectContent>
            {opciones.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.nombre} · {MARCA[c.brand] || c.brand}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {elegido && (
          <p className="text-[12.5px] text-ink" data-testid="merge-company-summary">
            {t("master_data.merge_summary", { origen: company?.nombre || "", destino: elegido.nombre })}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-8 rounded-[6px] text-[13px]">{t("app.cancel")}</Button>
          <Button data-testid="merge-company-confirm" disabled={!destino || busy} onClick={unir}
            className="h-8 rounded-[6px] bg-brand text-[13px] text-paper hover:bg-brand-hover">
            {t("master_data.merge_confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MasterDataReviewDialog = ({ open, onOpenChange }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const [destinos, setDestinos] = useState({});
  const [busy, setBusy] = useState(null);
  const [aConfirmar, setAConfirmar] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["master-data-review"],
    queryFn: async () => (await api.get("/companies/review")).data,
    enabled: open,
  });

  const refrescar = () => {
    qc.invalidateQueries({ queryKey: ["master-data-review"] });
    qc.invalidateQueries({ queryKey: ["companies"] });
    qc.invalidateQueries({ queryKey: ["properties"] });
  };

  const destinoDe = (grupo) => destinos[grupo.clave] || grupo.sugerida;

  const unirGrupo = async (grupo) => {
    const destino = destinoDe(grupo);
    setBusy(grupo.clave);
    try {
      for (const c of grupo.companias) {
        if (c.id !== destino) await api.post(`/companies/${c.id}/merge`, { into_id: destino });
      }
      toast.success(t("master_data.merged"));
      refrescar();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
      refrescar();
    } finally {
      setBusy(null);
    }
  };

  const pasarACasa = async (p) => {
    setBusy(p.id);
    try {
      const cambios = { tipo: "casa" };
      if (p.sugerencia?.nombre) cambios.nombre = p.sugerencia.nombre;
      await api.patch(`/properties/${p.id}`, cambios);
      toast.success(t("app.saved"));
      refrescar();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="master-data-review" className="max-h-[88vh] max-w-[860px] overflow-y-auto rounded-[8px]">
        <DialogHeader>
          <DialogTitle className="text-[16px]">{t("master_data.title")}</DialogTitle>
          <DialogDescription className="text-[12.5px] text-ink-secondary">{t("master_data.desc")}</DialogDescription>
        </DialogHeader>
        {isLoading || !data ? (
          <div className="loading-bar" />
        ) : (
          <div className="space-y-4">
            <Seccion testId="review-repetidas" titulo={t("master_data.repeated")} desc={t("master_data.repeated_desc")}
              n={data.companias_repetidas.length}>
              <div className="space-y-3">
                {data.companias_repetidas.map((g) => (
                  <div key={g.clave} className="border border-line p-2.5" data-testid={`review-group-${g.clave}`}>
                    <div className="space-y-1">
                      {g.companias.map((c) => (
                        <label key={c.id} className="flex items-center gap-2 text-[12.5px]">
                          <input type="radio" name={`destino-${g.clave}`} checked={(destinos[g.clave] || g.sugerida) === c.id}
                            onChange={() => setDestinos((d) => ({ ...d, [g.clave]: c.id }))} />
                          <Link to={`/companias/${c.id}`} className="font-medium text-ink hover:text-brand">{c.nombre}</Link>
                          <span className="text-ink-tertiary">
                            {MARCA[c.brand] || c.brand} · {t("master_data.n_buildings", { n: c.edificios })} · {t("master_data.n_orders", { n: c.ordenes })}
                            {c.status !== "active" ? ` · ${t(`status.${c.status}`)}` : ""}
                          </span>
                        </label>
                      ))}
                    </div>
                    <Button data-testid={`review-merge-${g.clave}`} size="sm" disabled={busy === g.clave} onClick={() => setAConfirmar(g)}
                      className="mt-2 h-7 rounded-[6px] bg-brand text-[12px] text-paper hover:bg-brand-hover">
                      {t("master_data.merge_group")}
                    </Button>
                  </div>
                ))}
              </div>
            </Seccion>

            <Seccion testId="review-casas" titulo={t("master_data.houses")} desc={t("master_data.houses_desc")}
              n={data.casas_como_edificio.length}>
              <table className="w-full text-[12.5px]">
                <tbody>
                  {data.casas_como_edificio.map((p) => (
                    <tr key={p.id} className="border-b border-line">
                      <td className="py-1.5 pr-2"><Link to={`/edificios/${p.id}`} className="font-medium text-ink hover:text-brand">{p.nombre}</Link></td>
                      <td className="py-1.5 pr-2 text-ink-tertiary">{formatAddress(p.direccion)}</td>
                      <td className="py-1.5 pr-2 text-ink-tertiary">
                        {p.sugerencia?.nombre ? t("master_data.rename_to", { nombre: p.sugerencia.nombre }) : t("master_data.keep_name")}
                      </td>
                      <td className="py-1.5 text-right">
                        <Button data-testid={`review-house-${p.id}`} size="sm" variant="outline" disabled={busy === p.id}
                          onClick={() => pasarACasa(p)} className="h-7 rounded-[6px] text-[12px]">
                          {t("master_data.to_house")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Seccion>

            <Seccion testId="review-compania-edificio" titulo={t("master_data.company_is_building")}
              desc={t("master_data.company_is_building_desc")} n={data.compania_es_edificio.length}>
              <ul className="space-y-1 text-[12.5px]">
                {data.compania_es_edificio.map((c) => (
                  <li key={c.id}><Link to={`/companias/${c.id}`} className="text-ink hover:text-brand">{c.nombre}</Link>
                    <span className="text-ink-tertiary"> · {MARCA[c.brand] || c.brand}</span></li>
                ))}
              </ul>
            </Seccion>

            <Seccion testId="review-trabajo" titulo={t("master_data.looks_like_job")} desc={t("master_data.looks_like_job_desc")}
              n={data.parecen_trabajo.length}>
              <ul className="space-y-1 text-[12.5px]">
                {data.parecen_trabajo.map((x) => (
                  <li key={`${x.entidad}-${x.id}`}>
                    <Link to={x.entidad === "compania" ? `/companias/${x.id}` : `/edificios/${x.id}`} className="text-ink hover:text-brand">{x.nombre}</Link>
                    <span className="text-ink-tertiary"> · {t(`master_data.entity_${x.entidad}`)}</span>
                  </li>
                ))}
              </ul>
            </Seccion>

            <Seccion testId="review-direccion" titulo={t("master_data.pending_address")} desc={t("master_data.pending_address_desc")}
              n={data.direccion_pendiente.length}>
              <ul className="space-y-1 text-[12.5px]">
                {data.direccion_pendiente.map((p) => (
                  <li key={p.id}><Link to={`/edificios/${p.id}`} className="text-ink hover:text-brand">{p.nombre}</Link>
                    <span className="text-ink-tertiary"> · {p.company_nombre}</span></li>
                ))}
              </ul>
            </Seccion>
          </div>
        )}
        <ConfirmDialog
          open={!!aConfirmar}
          onOpenChange={(v) => !v && setAConfirmar(null)}
          testId="review-merge-confirm"
          title={t("master_data.merge_group")}
          description={aConfirmar ? t("master_data.merge_group_confirm", {
            destino: aConfirmar.companias.find((c) => c.id === destinoDe(aConfirmar))?.nombre || "" }) : ""}
          onConfirm={() => { const g = aConfirmar; setAConfirmar(null); unirGrupo(g); }}
        />
      </DialogContent>
    </Dialog>
  );
};
